import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Grid,
    IconButton,
    Stack,
    Typography,
    Tabs,
    Tab,
    Paper,
    Button,
    TextField,
    Autocomplete,
    Chip,
    Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { GoogleMap, Marker } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { CustomerTypes, UserTypes } from '../../types';
import { Team } from '../../types/teams.types';
import { Tag } from '../../types/tags.types';
import { RootState } from '../../store';
import {
    APP_STATUS_META,
    deriveAppStatus,
} from '../lists/users-list/app-status';
import { useUserFormik } from '../add-new-user-modal/use-user-formik';
import { TagAutocompleteWithCreate } from '../shared/tag-autocomplete-with-create';
import { getCachedAddress, setCachedAddress } from './reverse-geocode-cache';

interface Props {
    visible: boolean;
    onClose(): void;
    user: UserTypes.Model;
    allTeams: Team[];
    canRemove?: boolean;
    customerSettings?: CustomerTypes.OnboardingForm;
    isCustomerSettingsLoading?: boolean;
    onSaved?(): void;
    onRemove?(): void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            {title}
        </Typography>
        <Grid container spacing={2}>
            {children}
        </Grid>
    </Paper>
);

const INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        fontSize: '14px',
        borderRadius: '8px',
        backgroundColor: '#fff',
    },
};

const EditableField: React.FC<{
    label: string;
    required?: boolean;
    md?: number;
    children: React.ReactNode;
}> = ({ label, required, md = 6, children }) => (
    <Grid item xs={12} md={md}>
        <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
                {required && (
                    <Typography
                        component="span"
                        sx={{ color: 'error.main', ml: 0.5, fontSize: 12 }}
                    >
                        *
                    </Typography>
                )}
            </Typography>
            {children}
        </Box>
    </Grid>
);

const DetailRow: React.FC<{
    label: string;
    value: React.ReactNode;
    md?: number;
}> = ({ label, value, md = 6 }) => (
    <Grid item xs={12} md={md}>
        <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
            </Typography>
            <Typography variant="body1">
                {value === null || value === undefined || value === '' ? 'N/A' : value}
            </Typography>
        </Box>
    </Grid>
);

const StatusChip: React.FC<{ user: UserTypes.Model }> = ({ user }) => {
    const status = deriveAppStatus(user);
    const meta = APP_STATUS_META[status];
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor:
                    status === 'on'
                        ? 'rgba(37,182,96,0.12)'
                        : status === 'pending'
                        ? 'rgba(255,193,7,0.14)'
                        : 'rgba(145,158,171,0.18)',
                color: 'grey.800',
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: meta.color,
                    boxShadow: `0 0 0 3px ${meta.ring}`,
                }}
            />
            {meta.mobileApp} · {meta.label}
        </Box>
    );
};

const formatDate = (raw: Date | string | null | undefined): string => {
    if (!raw) return 'N/A';
    const d = dayjs(raw);
    if (!d.isValid() || d.year() < 2000) return 'N/A';
    return d.format('LLL');
};

// Every MongoDB record id embeds its creation time (first 4 bytes = seconds
// since epoch). Users added one at a time before the API stamped createdAt
// carry no createdAt field, so fall back to the id and the drawer still shows
// when the user was added.
const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;
const createdAtFromObjectId = (id: string | null | undefined): Date | null => {
    if (!id || !OBJECT_ID_RE.test(id)) return null;
    return new Date(parseInt(id.slice(0, 8), 16) * 1000);
};

// The mobile app sometimes writes raw coordinates into the address field
// (e.g. "41.55262942, -73.06413719"). Treat those as missing so we don't
// render the coords twice and so the auto-resolve effect kicks in.
const COORD_STRING_RE = /^\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*$/;

export const UserDetailsDrawer: React.FC<Props> = ({
    visible,
    user,
    onClose,
    allTeams,
    canRemove,
    customerSettings,
    isCustomerSettingsLoading,
    onSaved,
    onRemove,
}) => {
    const [tab, setTab] = useState(0);
    const [editing, setEditing] = useState(false);
    // Optimistic snapshot of tags right after save. The parent only refetches
    // the list, not the selectedUser passed in here, so user.tags lags behind
    // until the drawer is reopened. This holds the just-saved tags so view
    // mode shows them immediately. Cleared when a different user is loaded.
    const [savedTagsSnapshot, setSavedTagsSnapshot] = useState<Tag[] | null>(null);

    // Reset to the first tab + view mode whenever the drawer is reopened —
    // otherwise sticky state surprises admins switching between users.
    useEffect(() => {
        if (visible) {
            setTab(0);
            setEditing(false);
        }
    }, [visible, user?.id]);

    useEffect(() => {
        setSavedTagsSnapshot(null);
    }, [user?.id]);

    const { items: availableTags } = useSelector((state: RootState) => state.tags);

    const userTeam = allTeams?.find((team) => (team as any)?.id === user?.teamId);

    // Form state for in-place editing. Hook runs unconditionally; the UI
    // only surfaces inputs when `editing` is true. Customer settings drive
    // the validation schema, so we fall back to an empty object so the hook
    // can always be called.
    const form = useUserFormik({
        selectedUser: user,
        allTeams,
        customerSettings: customerSettings ?? ({} as CustomerTypes.OnboardingForm),
        onSuccess: () => {
            setSavedTagsSnapshot(form.selectedTags);
            setEditing(false);
            onSaved?.();
        },
    });
    const {
        formik,
        selectedTeam: editTeam,
        setSelectedTeam: setEditTeam,
        selectedTags: editTags,
        setSelectedTags: setEditTags,
        availableTags: allAvailableTags,
        isLoading: saving,
        handlePhoneChange,
        isPhoneRequired,
        isEmployeeIdRequired,
        isEmailRequired,
    } = form;

    // View-mode tags: prefer the optimistic snapshot from a successful save
    // until the parent refetches the user object.
    const userTags = savedTagsSnapshot
        ?? (availableTags?.filter((tag) =>
            user?.tags?.includes(tag?._id!),
        ) ?? []);

    const lat = Number(user?.mobileLocation?.latitude);
    const lng = Number(user?.mobileLocation?.longitude);
    const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng) && (lat !== 0 || lng !== 0);

    const devices = user?.devices ?? [];

    // Edit is gated until customer settings have loaded — the form's
    // required-field rules (phone vs employee ID vs email) come from
    // userUniqueIdentifier, so editing without it would mis-validate.
    const canEdit = !!customerSettings && !isCustomerSettingsLoading;

    // Reverse-geocode on demand. Address is often missing on user.mobileLocation
    // even when coords are present, so we let the admin click to resolve.
    // Results are cached in localStorage keyed by rounded coordinates so the
    // same point doesn't bill the API twice.
    const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
    const [resolving, setResolving] = useState(false);
    const [resolveError, setResolveError] = useState<string | null>(null);

    useEffect(() => {
        // Reset whenever the user changes — addresses are per-coord, not
        // sticky across users.
        setResolvedAddress(null);
        setResolveError(null);
        setResolving(false);
    }, [user?.id]);

    const reverseGeocodeKey = hasCoords
        ? `pom:reverse-geocode:${lat.toFixed(5)},${lng.toFixed(5)}`
        : null;

    // Auto-fire the lookup when the Locations tab is opened and we don't
    // already have a real address. This is the "click on Locations tab"
    // trigger — gated on hasCoords so it stays a no-op for users without
    // any coordinates at all.
    useEffect(() => {
        if (tab !== 2 || !hasCoords || !reverseGeocodeKey) return;

        // Skip if the user document already has a real (non-coord) address.
        const stored = user?.mobileLocation?.address?.trim();
        if (stored && !COORD_STRING_RE.test(stored)) return;

        // We deliberately don't short-circuit on `resolvedAddress` here:
        // the reset effect on user-change schedules `setResolvedAddress(null)`
        // but that hasn't committed when this effect runs in the same
        // flush, so the prior user's value would block geocoding the new
        // user. The in-memory cache below makes a redundant re-run cheap
        // (no API call) which removes the need for that guard.

        let cancelled = false;
        const run = async () => {
            const cached = getCachedAddress(reverseGeocodeKey);
            if (cached) {
                if (!cancelled) setResolvedAddress(cached);
                return;
            }

            if (!window.google?.maps?.Geocoder) {
                if (!cancelled) setResolveError('Maps not loaded yet.');
                return;
            }

            if (!cancelled) setResolving(true);
            try {
                const geocoder = new window.google.maps.Geocoder();
                const result = await geocoder.geocode({ location: { lat, lng } });
                const address = result.results?.[0]?.formatted_address ?? null;
                if (cancelled) return;
                if (!address) {
                    setResolveError('No address found for these coordinates.');
                    return;
                }
                setResolvedAddress(address);
                setCachedAddress(reverseGeocodeKey, address);
            } catch (err) {
                console.error('[UserDetailsDrawer] Reverse-geocode failed:', err);
                if (!cancelled) setResolveError('Could not look up address.');
            } finally {
                if (!cancelled) setResolving(false);
            }
        };
        run();

        return () => {
            cancelled = true;
        };
        // Why: we deliberately only re-run when the tab, user, or coord-key
        // change. The in-memory cache makes redundant runs cheap, so we
        // don't need `resolvedAddress` in the deps to prevent re-firing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, user?.id, reverseGeocodeKey]);

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', md: '50%' } },
            }}
        >
            <Box sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {editing ? 'Edit User Details' : 'User Details'}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {!editing ? (
                            <>
                                <Tooltip
                                    title={
                                        canEdit
                                            ? ''
                                            : 'Loading customer settings…'
                                    }
                                >
                                    <span>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={
                                                <EditOutlinedIcon fontSize="small" />
                                            }
                                            disabled={!canEdit}
                                            onClick={() => {
                                                formik.resetForm();
                                                setTab(0);
                                                setEditing(true);
                                            }}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Edit
                                        </Button>
                                    </span>
                                </Tooltip>
                                {canRemove && onRemove && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={
                                            <PersonRemoveOutlinedIcon fontSize="small" />
                                        }
                                        onClick={onRemove}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        formik.resetForm();
                                        setEditing(false);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    size="small"
                                    variant="contained"
                                    loading={saving}
                                    onClick={() => formik.submitForm()}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Save
                                </LoadingButton>
                            </>
                        )}
                        <IconButton onClick={onClose} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 3 }}
                >
                    <Tab label="User Information" />
                    <Tab label="User Device" />
                    {!user?.mobileLocationHidden && <Tab label="Locations" />}
                </Tabs>

                {tab === 0 && (
                    <Stack spacing={2}>
                        <Section title="Basic Information">
                            {editing ? (
                                <>
                                    <EditableField label="First Name" required>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={INPUT_SX}
                                            {...formik.getFieldProps('firstName')}
                                            error={
                                                Boolean(formik.errors.firstName) &&
                                                formik.touched.firstName
                                            }
                                            helperText={
                                                formik.touched.firstName && formik.errors.firstName
                                            }
                                        />
                                    </EditableField>
                                    <EditableField label="Last Name" required>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={INPUT_SX}
                                            {...formik.getFieldProps('lastName')}
                                            error={
                                                Boolean(formik.errors.lastName) &&
                                                formik.touched.lastName
                                            }
                                            helperText={
                                                formik.touched.lastName && formik.errors.lastName
                                            }
                                        />
                                    </EditableField>
                                    <EditableField
                                        label="Phone Number"
                                        required={isPhoneRequired}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={INPUT_SX}
                                            value={formik.values.phoneNumber}
                                            onChange={(e) => handlePhoneChange(e.target.value)}
                                            error={
                                                Boolean(formik.errors.phoneNumber) &&
                                                formik.touched.phoneNumber
                                            }
                                            helperText={
                                                formik.touched.phoneNumber &&
                                                formik.errors.phoneNumber
                                            }
                                        />
                                    </EditableField>
                                    <EditableField
                                        label="Employee ID"
                                        required={isEmployeeIdRequired}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={INPUT_SX}
                                            {...formik.getFieldProps('employeeId')}
                                            error={
                                                Boolean(formik.errors.employeeId) &&
                                                formik.touched.employeeId
                                            }
                                            helperText={
                                                formik.touched.employeeId &&
                                                formik.errors.employeeId
                                            }
                                        />
                                    </EditableField>
                                    <EditableField label="Backup Email" required={isEmailRequired}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={INPUT_SX}
                                            {...formik.getFieldProps('backupEmail')}
                                            error={
                                                Boolean(formik.errors.backupEmail) &&
                                                formik.touched.backupEmail
                                            }
                                            helperText={
                                                formik.touched.backupEmail &&
                                                formik.errors.backupEmail
                                            }
                                        />
                                    </EditableField>
                                    <EditableField label="Team">
                                        <Autocomplete
                                            size="small"
                                            options={allTeams || []}
                                            value={editTeam}
                                            getOptionLabel={(option) =>
                                                (option as any)?.name || ''
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                (option as any)?.id === (value as any)?.id
                                            }
                                            onChange={(_, value) => setEditTeam(value)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select a team..."
                                                    sx={INPUT_SX}
                                                />
                                            )}
                                            sx={{
                                                '& .MuiAutocomplete-inputRoot': {
                                                    borderRadius: '8px',
                                                },
                                            }}
                                        />
                                    </EditableField>
                                    <DetailRow
                                        label="Team Leader"
                                        value={
                                            editTeam?.leaders
                                                ?.map((l) => l.fullname)
                                                .filter(Boolean)
                                                .join(', ') || undefined
                                        }
                                    />
                                    <Grid item xs={12}>
                                        <Box sx={{ py: 1 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                Tags
                                            </Typography>
                                            <TagAutocompleteWithCreate
                                                availableTags={(allAvailableTags as Tag[]) || []}
                                                selectedTags={editTags}
                                                onChange={setEditTags}
                                                inputSx={INPUT_SX}
                                            />
                                        </Box>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <DetailRow
                                        label="Full Name"
                                        md={12}
                                        value={
                                            [user?.firstName, user?.lastName]
                                                .filter(Boolean)
                                                .join(' ') || 'N/A'
                                        }
                                    />
                                    <DetailRow
                                        label="Phone Number"
                                        value={user?.phoneNumber}
                                    />
                                    <DetailRow
                                        label="Employee ID"
                                        value={user?.employeeId}
                                    />
                                    <DetailRow
                                        label="Backup Email"
                                        value={user?.backupEmail}
                                    />
                                    <DetailRow label="Team" value={userTeam?.name} />
                                    <DetailRow
                                        label="Team Leader"
                                        value={
                                            userTeam?.leaders
                                                ?.map((l) => l.fullname)
                                                .filter(Boolean)
                                                .join(', ') || undefined
                                        }
                                    />
                                    <DetailRow
                                        label="Created At"
                                        value={formatDate(
                                            user?.createdAt ?? createdAtFromObjectId(user?.id),
                                        )}
                                    />
                                    {userTags.length > 0 && (
                                        <Grid item xs={12}>
                                            <Box sx={{ py: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    gutterBottom
                                                >
                                                    Tags
                                                </Typography>
                                                <Box
                                                    display="flex"
                                                    flexWrap="wrap"
                                                    gap={0.75}
                                                >
                                                    {userTags.map((tag) => {
                                                        const color = tag.color || '#637381';
                                                        return (
                                                            <Box
                                                                key={tag._id}
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    px: 1,
                                                                    height: 24,
                                                                    borderRadius: '12px',
                                                                    backgroundColor: `${color}22`,
                                                                    color,
                                                                    fontSize: 12,
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {tag.name}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Section>

                        <Section title="App Information">
                            <Grid item xs={12} md={6}>
                                <Box sx={{ py: 1 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        App Status
                                    </Typography>
                                    <StatusChip user={user} />
                                </Box>
                            </Grid>
                            <DetailRow label="App Version" value={user?.appVersion} />
                        </Section>

                        <Section title="Emergency Contact">
                            <DetailRow
                                label="First Name"
                                value={user?.emergencyContact?.contactFirstName}
                            />
                            <DetailRow
                                label="Last Name"
                                value={user?.emergencyContact?.contactLastName}
                            />
                            <DetailRow
                                label="Phone Number"
                                value={user?.emergencyContact?.contactPhoneNumber}
                                md={12}
                            />
                        </Section>

                        <Section title="SOS Settings">
                            <DetailRow
                                label="Alert SOS Connections"
                                value={user?.sosSettings?.alertSOSConnections ? 'Yes' : 'No'}
                            />
                            <DetailRow
                                label="Dispatch Enabled"
                                value={user?.sosSettings?.dispatchEnabled ? 'Yes' : 'No'}
                            />
                        </Section>
                    </Stack>
                )}

                {tab === 1 && (
                    <Stack spacing={2}>
                        {devices.length === 0 ? (
                            <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50' }}>
                                <Typography color="text.secondary" textAlign="center">
                                    No paired device.
                                </Typography>
                            </Paper>
                        ) : (
                            devices.map((device, idx) => (
                                <Section
                                    key={(device as any)?.id ?? idx}
                                    title={
                                        devices.length > 1
                                            ? `Device ${idx + 1}`
                                            : 'Paired Device'
                                    }
                                >
                                    <DetailRow
                                        label="Serial Number"
                                        value={
                                            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                                {device?.serialNumber ?? 'N/A'}
                                            </span>
                                        }
                                    />
                                    <DetailRow
                                        label="Battery Level"
                                        value={
                                            (device as any)?.batteryLevel != null
                                                ? `${(device as any).batteryLevel}%`
                                                : 'N/A'
                                        }
                                    />
                                    <DetailRow
                                        label="Last Updated"
                                        value={formatDate((device as any)?.updatedAt)}
                                    />
                                    <DetailRow
                                        label="Paired Date"
                                        md={12}
                                        value={formatDate((device as any)?.createdAt)}
                                    />
                                </Section>
                            ))
                        )}
                    </Stack>
                )}

                {tab === 2 && !user?.mobileLocationHidden && (
                    <Stack spacing={2}>
                        <Section title="Mobile Location">
                            <Grid item xs={12}>
                                <Box sx={{ py: 1 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Address
                                    </Typography>
                                    {(() => {
                                        const stored = user?.mobileLocation?.address?.trim();
                                        const storedIsRealAddress =
                                            !!stored && !COORD_STRING_RE.test(stored);
                                        if (storedIsRealAddress) {
                                            return (
                                                <Typography variant="body1">{stored}</Typography>
                                            );
                                        }
                                        if (resolvedAddress) {
                                            return (
                                                <Typography variant="body1">
                                                    {resolvedAddress}
                                                </Typography>
                                            );
                                        }
                                        if (!hasCoords) {
                                            return <Typography variant="body1">N/A</Typography>;
                                        }
                                        if (resolving) {
                                            return (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                >
                                                    Looking up address…
                                                </Typography>
                                            );
                                        }
                                        if (resolveError) {
                                            return (
                                                <Typography variant="body1" color="error">
                                                    {resolveError}
                                                </Typography>
                                            );
                                        }
                                        return <Typography variant="body1">N/A</Typography>;
                                    })()}
                                </Box>
                            </Grid>
                            <DetailRow
                                label="Last Updated"
                                md={12}
                                value={formatDate(user?.mobileLocation?.updatedAt)}
                            />
                        </Section>
                        {hasCoords ? (
                            <Box sx={{ height: 400, borderRadius: 1, overflow: 'hidden' }}>
                                <GoogleMap
                                    center={{ lat, lng }}
                                    mapContainerStyle={{ height: '100%', width: '100%' }}
                                    zoom={15}
                                >
                                    <Marker position={{ lat, lng }} />
                                </GoogleMap>
                            </Box>
                        ) : (
                            <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50' }}>
                                <Typography color="text.secondary" textAlign="center">
                                    No location available.
                                </Typography>
                            </Paper>
                        )}
                    </Stack>
                )}
            </Box>
        </Drawer>
    );
};
